import React from "react";
import { onCreateSubmit, useApplyCreate } from "@hex-insights/app-modules";
import {
	Button,
	ButtonProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	pickKeys,
	Row,
	Section,
	useConditionalCloseConfirmation,
	useUUID,
} from "@hex-insights/core";
import {
	Form,
	FormSubmissionResult,
	FormSubmitFunction,
	FormType,
	SubmissionStatus,
	submissionSuccess,
	SubmitButton,
	useFormNotifications,
	useFormState,
	useOnSuccess,
} from "@hex-insights/forms";
import { FlexGap } from "../../../../Components/FlexGap";
import { Icon } from "../../../../Components/Icon";
import {
	AcademicTermOrderField,
	ContractCreateMutation,
	OrderDirection,
	useAcademicTermNamesQuery,
	useStudentNameAndImageQuery,
} from "../../../../Utilities";
import { AcademicTermFormat, ContractFormat, ContractLineItemFormat, StudentFormat } from "../../../Format";
import { ContractField } from "../../../Forms";
import { ContractFormValues, ContractLineItemFormValues, ContractMutation } from "../../../FormUtilities";
import { ContractUtilities } from "../../../Utilities";
import { ContractInstallmentsFormTable } from "../../ContractInstallment";
import { ContractLineItemsFormTable } from "../../ContractLineItem";
import { StudentIcon } from "../../Student";
import { ContractWizardNavigation, ContractWizardPage, useContractWizardNavigation } from "./Navigation";
import { SubFormSuccessNotification, SubFormValidationFailureNotification } from "./Notifications";
import styles from "./styles.module.css";

export type ContractCreateModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<ContractCreateModalProps, "initialFormValues" | "onSuccess">;

export function ContractCreateModalController({
	initialFormValues,
	onSuccess,
	...props
}: ContractCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} {...props} />

			<Modal.If condition={isModalOpen}>
				<ContractCreateModal initialFormValues={initialFormValues} onSuccess={onSuccess} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

export type ContractCreateModalProps = {
	initialFormValues?: Partial<ContractFormValues.Create>;
	onSuccess?: (result: ContractCreateMutation["createContract"]) => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ContractCreateModal({
	initialFormValues,
	onSuccess,
	ifRef,
	onClose,
	className,
	style,
}: ContractCreateModalProps) {
	const wizardSessionID = useUUID();

	const [formValues, setFormValues] = React.useState(() => ({
		...ContractFormValues.initialCreate,
		contractLineItems: [ContractLineItemFormValues.blankItemInContract],
		...initialFormValues,
	}));

	const [wasSubmittedSuccessfully, setWasSubmittedSuccessfully] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const { addSuccessNotification, addSubmissionFailureNotification } = useFormNotifications();
	const create = ContractMutation.useCreate();
	const { applyCreate, resultRef } = useApplyCreate(create);
	const onSubmit = React.useCallback(
		async (formValues: ContractFormValues.Create) => {
			setIsSubmitting(true);

			const result = await onCreateSubmit(formValues, applyCreate);
			if (FormSubmissionResult.isSuccess(result)) {
				setWasSubmittedSuccessfully(true);
				addSuccessNotification();
			} else {
				addSubmissionFailureNotification();
			}

			setIsSubmitting(false);
			return result;
		},
		[applyCreate, addSuccessNotification, addSubmissionFailureNotification],
	);
	React.useEffect(() => {
		if (onSuccess && wasSubmittedSuccessfully && resultRef.current) {
			onSuccess(resultRef.current);
		}
	}, [wasSubmittedSuccessfully, resultRef, onSuccess]);

	let submissionStatus = SubmissionStatus.Idle;
	if (isSubmitting) {
		submissionStatus = SubmissionStatus.Submitting;
	}
	if (wasSubmittedSuccessfully) {
		submissionStatus = SubmissionStatus.Success;
	}

	return (
		<ContractWizardNavigation.Router>
			<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
				<Modal.Header className={styles["modal__header"]}>
					<Heading.H2 noMargin>New Contract</Heading.H2>
				</Modal.Header>
				<ModalCloseConfirmer />
				<ContractWizardNavigation.Route page={ContractWizardPage.General}>
					<ContractCreateGeneralPage
						formValues={formValues}
						setFormValues={setFormValues}
						wizardSessionID={wizardSessionID}
					/>
				</ContractWizardNavigation.Route>
				<ContractWizardNavigation.Route page={ContractWizardPage.Services}>
					<ContractCreateServicesPage
						formValues={formValues}
						setFormValues={setFormValues}
						wizardSessionID={wizardSessionID}
					/>
				</ContractWizardNavigation.Route>
				<ContractWizardNavigation.Route page={ContractWizardPage.PaymentSchedule}>
					<ContractCreatePaymentSchedulePage
						formValues={formValues}
						setFormValues={setFormValues}
						wizardSessionID={wizardSessionID}
					/>
				</ContractWizardNavigation.Route>
				<ContractWizardNavigation.Route page={ContractWizardPage.Review}>
					<ContractCreateReviewPage formValues={formValues} onSubmit={onSubmit} submissionStatus={submissionStatus} />
				</ContractWizardNavigation.Route>
			</Modal>
		</ContractWizardNavigation.Router>
	);
}

function ModalCloseConfirmer() {
	const { page } = useContractWizardNavigation();
	useConditionalCloseConfirmation(page !== ContractWizardPage.General);
	return null;
}

type ContractCreatePageProps = {
	formValues: ContractFormValues.Create;
	setFormValues: React.Dispatch<React.SetStateAction<ContractFormValues.Create>>;
	wizardSessionID: string;
};

type ContractCreateGeneralFormValues = Pick<
	ContractFormValues.Create,
	"studentID" | "name" | "effectiveDate" | "academicTermIDs"
>;

function ContractCreateGeneralPage({ formValues, setFormValues, wizardSessionID }: ContractCreatePageProps) {
	const { incrementPage } = useContractWizardNavigation();

	const formState = useFormState<ContractCreateGeneralFormValues>({
		initialFormValues: () => pickKeys(formValues, ["studentID", "name", "effectiveDate", "academicTermIDs"]),
		formType: FormType.Create,
	});

	const onSubmit = React.useCallback<FormSubmitFunction<ContractCreateGeneralFormValues>>(
		async (formState) => {
			setFormValues((prev) => ({ ...prev, ...formState.formValues }));
			return submissionSuccess();
		},
		[setFormValues],
	);
	useOnSuccess(formState, incrementPage);

	return (
		<React.Fragment>
			<Modal.Body className={styles["modal__body"]}>
				<Row className={styles["main-row"]}>
					<ContractWizardNavigation />
					<div className={styles["content"]}>
						<Form
							name={`contract.create.general.${wizardSessionID}`}
							formState={formState}
							onSubmit={formState.onSubmitWrapper(onSubmit)}
							SubmissionSuccessNotification={SubFormSuccessNotification}
							ValidationFailureNotification={SubFormValidationFailureNotification}
							mergeUnsavedChangesAutomatically
						>
							<Form.Column>
								<ContractField.Student.Select formState={formState} />
								<ContractField.Name formState={formState} />
								<ContractField.EffectiveDate formState={formState} />
								<ContractField.AcademicTerms.Select formState={formState} />
							</Form.Column>
						</Form>
					</div>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer"]}>
				<Row justify="spaced-end">
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Next
					</SubmitButton>
				</Row>
			</Modal.Footer>
		</React.Fragment>
	);
}

type ContractCreateServicesFormValues = Pick<ContractFormValues.Create, "contractLineItems">;

function ContractCreateServicesPage({ formValues, setFormValues, wizardSessionID }: ContractCreatePageProps) {
	const { incrementPage, decrementPage } = useContractWizardNavigation();

	const formState = useFormState<ContractCreateServicesFormValues>({
		initialFormValues: () => pickKeys(formValues, ["contractLineItems"]),
		formType: FormType.Create,
	});

	const onSubmit = React.useCallback<FormSubmitFunction<ContractCreateServicesFormValues>>(
		async (formState) => {
			setFormValues((prev) => ({ ...prev, ...formState.formValues }));
			return submissionSuccess();
		},
		[setFormValues],
	);
	useOnSuccess(formState, incrementPage);

	const totalPrice = React.useMemo(
		() => ContractUtilities.calculateTotalFormPrice({ contractLineItems: formState.formValues.contractLineItems }),
		[formState.formValues.contractLineItems],
	);

	return (
		<React.Fragment>
			<Modal.Body className={styles["modal__body"]}>
				<Row className={styles["main-row"]}>
					<ContractWizardNavigation />
					<div className={styles["content"]}>
						<Form
							name={`contract.create.services.${wizardSessionID}`}
							formState={formState}
							onSubmit={formState.onSubmitWrapper(onSubmit)}
							SubmissionSuccessNotification={SubFormSuccessNotification}
							ValidationFailureNotification={SubFormValidationFailureNotification}
							mergeUnsavedChangesAutomatically
						>
							<Form.Column style={{ width: "100%" }}>
								<ContractField.ContractLineItems.SubForm formState={formState} />
								<span>
									Total Contract Price: <strong>{ContractLineItemFormat.Fields.amount(totalPrice)}</strong>
								</span>
							</Form.Column>
						</Form>
					</div>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer"]}>
				<Row justify="spaced-end">
					<Button variant="secondary" onClick={decrementPage}>
						Back
					</Button>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Next
					</SubmitButton>
				</Row>
			</Modal.Footer>
		</React.Fragment>
	);
}

type ContractCreatePaymentScheduleFormValues = Pick<ContractFormValues.Create, "contractInstallments">;

function ContractCreatePaymentSchedulePage({ formValues, setFormValues, wizardSessionID }: ContractCreatePageProps) {
	const { incrementPage, decrementPage } = useContractWizardNavigation();

	const formState = useFormState<ContractCreatePaymentScheduleFormValues>({
		initialFormValues: () => pickKeys(formValues, ["contractInstallments"]),
		formType: FormType.Create,
	});

	const onSubmit = React.useCallback<FormSubmitFunction<ContractCreatePaymentScheduleFormValues>>(
		async (formState) => {
			setFormValues((prev) => ({ ...prev, ...formState.formValues }));
			return submissionSuccess();
		},
		[setFormValues],
	);
	useOnSuccess(formState, incrementPage);

	return (
		<React.Fragment>
			<Modal.Body className={styles["modal__body"]}>
				<Row className={styles["main-row"]}>
					<ContractWizardNavigation />
					<div className={styles["content"]}>
						<Form
							name={`contract.create.services.${wizardSessionID}`}
							formState={formState}
							onSubmit={formState.onSubmitWrapper(onSubmit)}
							SubmissionSuccessNotification={SubFormSuccessNotification}
							ValidationFailureNotification={SubFormValidationFailureNotification}
							mergeUnsavedChangesAutomatically
						>
							<Form.Column style={{ width: "100%" }}>
								<ContractField.ContractInstallments.SubForm formState={formState} />
							</Form.Column>
						</Form>
					</div>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer"]}>
				<Row justify="spaced-end">
					<Button variant="secondary" onClick={decrementPage}>
						Back
					</Button>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Next
					</SubmitButton>
				</Row>
			</Modal.Footer>
		</React.Fragment>
	);
}

type ContractCreateReviewPageProps = {
	formValues: ContractFormValues.Create;
	onSubmit: (formValues: ContractFormValues.Create) => void;
	submissionStatus: SubmissionStatus;
};

function ContractCreateReviewPage({ formValues, onSubmit, submissionStatus }: ContractCreateReviewPageProps) {
	const { decrementPage } = useContractWizardNavigation();

	const onSubmitAsDraft = React.useCallback(async () => {
		return onSubmit({ ...formValues, isFinalized: false });
	}, [onSubmit, formValues]);

	const onSubmitFinalized = React.useCallback(async () => {
		return onSubmit({ ...formValues, isFinalized: true });
	}, [onSubmit, formValues]);

	const { loading: loadingStudent, data: studentData } = useStudentNameAndImageQuery({
		variables: { id: formValues.studentID ?? "0" },
		skip: !formValues.studentID,
	});
	const student = studentData?.student;

	const { loading: loadingAcademicTerms, data: academicTermsData } = useAcademicTermNamesQuery({
		variables: {
			filters: { idIn: formValues.academicTermIDs },
			order: { field: AcademicTermOrderField.StartDate, direction: OrderDirection.Asc },
		},
		skip: formValues.academicTermIDs.length === 0,
	});
	const academicTermNames =
		academicTermsData?.academicTermConnection.edges.map((e) => AcademicTermFormat.name(e.node)).join(", ") ?? "";

	const totalPrice = React.useMemo(
		() => ContractUtilities.calculateTotalFormPrice({ contractLineItems: formValues.contractLineItems }),
		[formValues.contractLineItems],
	);

	return (
		<React.Fragment>
			<Modal.Body className={styles["modal__body"]}>
				<Row className={styles["main-row"]}>
					<ContractWizardNavigation />
					<Column justify="spaced-start" verticalSpacing="1rem" className={styles["content"]}>
						<Section className={styles["review-section"]}>
							<Section.Header className={styles["review-section__header"]}>
								<Heading.H3 noMargin>General</Heading.H3>
							</Section.Header>
							<Section.Body className={styles["review-section__body"]}>
								<Row overflow="wrap">
									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["review__label"]}>Student</span>
										<Conditional>
											<If condition={loadingStudent}>Loading...</If>
											<Else>
												{!!student && (
													<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
														<StudentIcon student={student} size={24} /> {StudentFormat.name(student)}
													</Row>
												)}
											</Else>
										</Conditional>
									</Column>

									<FlexGap minWidth="1rem" maxWidth="2.25rem" />

									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["review__label"]}>Contract ID</span>
										<span>{ContractFormat.Fields.name(formValues.name)}</span>
									</Column>

									<FlexGap minWidth="1rem" maxWidth="2.25rem" />

									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["review__label"]}>Effective Date</span>
										<span>{ContractFormat.Fields.effectiveDate(formValues.effectiveDate)}</span>
									</Column>

									<FlexGap minWidth="1rem" maxWidth="2.25rem" />

									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["review__label"]}>Academic Terms</span>
										<Conditional>
											<If condition={loadingAcademicTerms}>Loading...</If>
											<Else>{academicTermNames}</Else>
										</Conditional>
									</Column>
								</Row>
							</Section.Body>
						</Section>

						<Section className={styles["review-section"]}>
							<Section.Header className={styles["review-section__header"]}>
								<Heading.H3 noMargin>Services</Heading.H3>
							</Section.Header>
							<Section.Body className={styles["review-section__body"]}>
								<Column justify="spaced-start" verticalSpacing="1rem">
									<ContractLineItemsFormTable variant="admin" formValuesSets={formValues.contractLineItems} />

									<Row
										justify="spaced-start"
										align="center"
										horizontalSpacing="1rem"
										className={styles["review__services__total"]}
									>
										<Icon.Receipt size={24} color="var(--primary-color)" />
										<span>
											Total Contract Price: <strong>{ContractLineItemFormat.Fields.amount(totalPrice)}</strong>
										</span>
									</Row>
								</Column>
							</Section.Body>
						</Section>

						<Section className={styles["review-section"]}>
							<Section.Header className={styles["review-section__header"]}>
								<Heading.H3 noMargin>Payment Schedule</Heading.H3>
							</Section.Header>
							<Section.Body className={styles["review-section__body"]}>
								<ContractInstallmentsFormTable
									formValuesSets={formValues.contractInstallments}
									totalPrice={totalPrice}
								/>
							</Section.Body>
						</Section>
					</Column>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer"]}>
				<Row justify="spaced-end">
					<Button variant="secondary" onClick={decrementPage}>
						Back
					</Button>
					<SubmitButton variant="secondary" onClick={onSubmitAsDraft} submissionStatus={submissionStatus}>
						Save as Draft
					</SubmitButton>
					<SubmitButton onClick={onSubmitFinalized} submissionStatus={submissionStatus}>
						Finalize and Save
					</SubmitButton>
				</Row>
			</Modal.Footer>
		</React.Fragment>
	);
}
